import { Component, Input, AfterContentInit, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AppUser } from '../../../@core/models/app.user';
import { LoginService } from '../../../@core/data/login.service';
import { GeneralService } from '../../../@core/data/general.service';

import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { generalSettings } from '../../../../main/settings';
import { FirebaseHelper } from '../../../@core/services/firebase.helper';
import { DeviceDetectorService } from 'ngx-device-detector';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'edm-paywall',
  templateUrl: './paywall.component.html',
  styleUrls: ['./paywall.component.scss'],
  standalone: false
})

export class PaywallComponent implements OnInit, AfterContentInit, OnDestroy {

  @Input() model: string;
  @Input() closeWhenUrlChanges: boolean = false;
  @Input() contentId: string | null = null;
  @Input() contentType: 'product' | 'lesson' | null = null;

  // Variantes in-app: 'survey' (header + título, X escondido quando forceAnswer) e 'url' (página do produtor, sempre fechável).
  @Input() variant: 'checkout' | 'survey' | 'url' = 'checkout';
  @Input() headerTitle: string | null = null;
  @Input() forceAnswer: boolean = false;

  iframeSrc: SafeResourceUrl;
  loadedSrc: string;

  // altura do modal de checkout (90% da viewport); binding declarativo evita
  // depender de query ao DOM em ngAfterContentInit, que falha com o markup atrás de @if
  checkoutHeightPx: number | null = null;

  // survey usa altura fixa via CSS; a 1ª mensagem do iframe só sinaliza que renderizou
  // (libera o fade-in do iframe, evitando a caixa vazia durante o load)
  surveyReady: boolean = false;
  private surveyReadyFallback: number | null = null;

  private readonly destroy$ = new Subject<void>();

  private readonly messageHandler = (event: MessageEvent): void => {
    if (event.data === 'close') {
      this.closeModal('reload');
      return;
    }
    if (event.data === 'survey-navigate') {
      this.closeWhenUrlChanges = false;
      return;
    }
    if (this.variant !== 'survey') return;
    const data = event.data as { type?: string; height?: number };
    if (data && data.type === 'edm-survey-height' && typeof data.height === 'number') {
      this.markSurveyReady();
    }
  };

  constructor(
    private activeModal: NgbActiveModal,
    private loginService: LoginService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private firebaseHelper: FirebaseHelper,
    private deviceService: DeviceDetectorService,
    private generalService: GeneralService,
  ) { }

  ngOnInit(): void {
    this.firebaseHelper.logEvent("screen_view", { screen_name: "paywall" });
    const purchaseParams: Record<string, string> = {
      os: this.deviceService.getDeviceInfo().os,
      origin: 'web',
    };
    if (this.contentType === 'product') purchaseParams['product_id'] = this.contentId;
    if (this.contentType === 'lesson') purchaseParams['lesson_id'] = this.contentId;
    this.firebaseHelper.logEvent("click_purchase_intent", purchaseParams);
    window.addEventListener('message', this.messageHandler);
    if (this.loginService.isAuthenticated()) {
      this.loginService.loggedUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (user: AppUser): void => {
          if (this.exists(user)) {
            this.loginService.dispatchEvent('paywall-pwa', user);
          }
        }
      );
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler);
    this.clearSurveyReadyFallback();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private clearSurveyReadyFallback(): void {
    if (this.surveyReadyFallback !== null) {
      clearTimeout(this.surveyReadyFallback);
      this.surveyReadyFallback = null;
    }
  }

  // libera o fade-in do iframe assim que o survey renderiza (altura é fixa via CSS)
  private markSurveyReady(): void {
    if (this.surveyReady) return;
    this.surveyReady = true;
    this.clearSurveyReadyFallback();
    this.cdr.detectChanges();
  }

  ngAfterContentInit(): void {
    if (this.model === null || this.model === undefined) {
      this.connectionError();
    } else if (this.variant === 'checkout') {
      const height: number = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      this.checkoutHeightPx = Math.floor(height * 0.9);
    } else {
      // survey/url: rede de segurança que revela o iframe mesmo se a mensagem/load nunca chegar
      this.surveyReadyFallback = window.setTimeout(() => this.markSurveyReady(), 2500);
    }
    this.iframeSrc = this.safeUrl(this.model);
    this.cdr.detectChanges();
  }

  private connectionError(): void {
    this.router.navigate(['produtos']);
  }

  exists(object: any): boolean {
    return object !== null && object !== undefined && typeof object != 'undefined';
  }

  private safeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // Lê o iframe que disparou o load: o document pode ter outros (ex.: o <noscript><iframe> do GTM, prepended no body),
  // e ler o [0] do document fazia o modal fechar já no 1º load da survey.
  checkToClose(event: Event): void {
    // url: a página do produtor não emite edm-survey-height, então o load do iframe libera o fade-in.
    if (this.variant === 'url') this.markSurveyReady();
    const iframe: HTMLIFrameElement | null = event.target instanceof HTMLIFrameElement ? event.target : null;
    if (iframe !== null && iframe.getAttribute('src') !== null) {
      if (this.exists(this.loadedSrc) && this.closeWhenUrlChanges) {
        this.closeModal();
      } else {
        this.loadedSrc = iframe.getAttribute('src');
      }
    }
  }

  iconCustomLayoutClass(): string {
    return generalSettings.timelineIconsLayout + '-icon';
  }

  iconTypeLayoutClass(): string {
    return generalSettings.iconsType + '-icons-type';
  }

  // Título do header: name do detail; fallback "Avaliação" quando vazio.
  get surveyDisplayTitle(): string {
    return this.exists(this.headerTitle) && this.headerTitle.trim().length > 0
      ? this.headerTitle
      : this.generalService.getTranslate('general.inAppSurveyDefaultTitle');
  }

  // Aviso do footer quando o in-app é obrigatório (explica a ausência do "X").
  get surveyRequiredNotice(): string {
    return this.generalService.getTranslate('general.inAppSurveyRequiredNotice');
  }

  closeModal(data: string = null): void {
    const isFromExplorer: boolean = this.route.snapshot.queryParams['explorer'] === 'true';
    this.activeModal.close(data);
    if (isFromExplorer) {
      const anchor: string = sessionStorage.getItem('exploreClickedAnchor') || sessionStorage.getItem('exploreLastAnchor');
      const params: string = anchor
        ? `?explorer=true&anchor=${encodeURIComponent(anchor)}`
        : '?explorer=true';
      this.router.navigateByUrl(`/explorar${params}`);
    }
  }
}
